//Adevotees.js

import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import {
  FaCheck, FaTimes, FaInfoCircle, FaSearch, FaSync, FaFileExcel,
  FaEye, FaTrash, FaBan, FaUserCheck, FaIdCard, FaTimes as FaClose,
  FaPhone, FaEnvelope, FaMapMarkerAlt, FaCalendarAlt, FaCheckCircle,
  FaChevronDown, FaShieldAlt, FaExclamationTriangle, FaUserTimes,
  FaEdit, FaEyeSlash, FaSave
} from "react-icons/fa";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

/* ═══════════════════════════════════════════════════
   TOAST COMPONENT
═══════════════════════════════════════════════════ */
function Toast({ message, type, onClose }) {
  const { t } = useTranslation();
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const config = {
    success: { icon: <FaCheck />, label: t("toastBlessings"), accentColor: "#2a6b2a", iconBg: "rgba(42,107,42,0.12)", borderColor: "rgba(42,107,42,0.3)", symbol: "✦" },
    error:   { icon: <FaTimes />, label: t("toastError"),  accentColor: "#8b1a1a", iconBg: "rgba(139,26,26,0.1)",  borderColor: "rgba(139,26,26,0.28)", symbol: "✗" },
    info:    { icon: <FaInfoCircle />, label: t("toastDivineNotice"), accentColor: "#8a6020", iconBg: "rgba(201,151,58,0.1)", borderColor: "rgba(201,151,58,0.3)", symbol: "ॐ" }
  };
  const c = config[type] || config.info;

  return (
    <div className="adv-toast-container">
      <div className="adv-toast-card" style={{ "--accent": c.accentColor, "--border-color": c.borderColor }}>
        <div className="adv-toast-top-bar" />
        <span className="adv-toast-corner tl" /><span className="adv-toast-corner tr" />
        <span className="adv-toast-corner bl" /><span className="adv-toast-corner br" />
        <div className="adv-toast-medallion" style={{ background: c.iconBg, borderColor: c.borderColor }}>
          <div className="adv-toast-spin-ring" />
          <div className="adv-toast-icon-inner" style={{ color: c.accentColor }}>{c.icon}</div>
        </div>
        <div className="adv-toast-content">
          <div className="adv-toast-label-row">
            <span className="adv-toast-symbol" style={{ color: c.accentColor }}>{c.symbol}</span>
            <h4 className="adv-toast-title" style={{ color: c.accentColor }}>{c.label}</h4>
            <span className="adv-toast-symbol" style={{ color: c.accentColor }}>{c.symbol}</span>
          </div>
          <div className="adv-toast-rule" style={{ background: `linear-gradient(90deg,transparent,${c.accentColor}55,transparent)` }} />
          <p className="adv-toast-message">{message}</p>
        </div>
        <button className="adv-toast-close" onClick={onClose} style={{ color: c.accentColor }}>✕</button>
        <div className="adv-toast-bottom-rule" />
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   LOADING SPINNER
═══════════════════════════════════════════════════ */
function OmLoader() {
  return (
    <div className="adv-loader-wrap">
      <div className="adv-loader-rings">
        {[90, 70, 52].map((s, i) => (
          <div key={i} className="adv-loader-ring" style={{ width: s, height: s, animationDelay: `${i * 0.3}s` }} />
        ))}
        <div className="adv-loader-om">ॐ</div>
      </div>
      <p className="adv-loader-text">Loading Devotees...</p>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   CONFIRM DIALOG
═══════════════════════════════════════════════════ */
function ConfirmDialog({ message, onConfirm, onCancel, confirmLabel = "Confirm", danger = false, title = "Confirm Action", icon = null, subtitle = "" }) {
  const { t } = useTranslation();
  const iconEl = icon || (danger ? <FaExclamationTriangle /> : <FaShieldAlt />);
  const accentColor = danger ? "#8b1a1a" : "#2a6b2a";
  const accentBg = danger ? "rgba(139,26,26,0.12)" : "rgba(42,107,42,0.1)";
  const accentBorder = danger ? "rgba(139,26,26,0.3)" : "rgba(42,107,42,0.28)";

  return createPortal(
    <div className="adv-modal-overlay" onClick={onCancel}>
      <div className="adv-confirm-modal" onClick={e => e.stopPropagation()}>
        <div className="adv-cm-topstrip" style={{ background: `linear-gradient(90deg, transparent, ${accentColor}88, ${accentColor}, ${accentColor}88, transparent)` }} />
        <span className="adv-cm-corner tl" /><span className="adv-cm-corner tr" />
        <span className="adv-cm-corner bl" /><span className="adv-cm-corner br" />
        <div className="adv-cm-header">
          <div className="adv-cm-icon-wrap" style={{ background: accentBg, borderColor: accentBorder }}>
            <div className="adv-cm-icon-ring" style={{ borderColor: `${accentColor}44` }} />
            <div className="adv-cm-icon-ring adv-cm-icon-ring2" style={{ borderColor: `${accentColor}22` }} />
            <span className="adv-cm-icon" style={{ color: accentColor }}>{iconEl}</span>
          </div>
          <div className="adv-cm-title-area">
            <div className="adv-cm-eyebrow">✦ {t("modConfirmSub")} ✦</div>
            <h3 className="adv-cm-title">{title}</h3>
            {subtitle && <p className="adv-cm-subtitle">{subtitle}</p>}
          </div>
        </div>
        <div className="adv-cm-divider">
          <span className="adv-cm-divider-gem" style={{ background: accentColor }} />
        </div>
        <div className="adv-cm-body">
          <p className="adv-cm-message">{message}</p>
        </div>
        <div className="adv-cm-actions">
          <button className="adv-btn adv-btn-ghost" onClick={onCancel}>{t("btnCancel")}</button>
          <button
            className="adv-btn adv-cm-confirm-btn"
            style={{ "--cbg": danger ? "linear-gradient(135deg,#5a0000,#8b1a1a)" : "linear-gradient(135deg,#1a4a1a,#2a6b2a)", "--cborder": accentBorder }}
            onClick={onConfirm}
          >
            {iconEl} {confirmLabel}
          </button>
        </div>
        <div className="adv-cm-bottomstrip" />
      </div>
    </div>,
    document.body
  );
}

/* ═══════════════════════════════════════════════════
   ID PROOF VIEWER MODAL
═══════════════════════════════════════════════════ */
function IdProofModal({ path, name, onClose }) {
  const { t } = useTranslation();
  const isImage = path && /\.(jpg|jpeg|png|gif|webp)$/i.test(path);
  const isPdf   = path && /\.pdf$/i.test(path);
const src = path?.startsWith("http")
  ? path
  : `${process.env.REACT_APP_API_URL || "http://localhost:5000"}/${path}`;
  return createPortal(
    <div className="adv-modal-overlay" onClick={onClose}>
      <div className="adv-regal-modal adv-idproof-regal" onClick={e => e.stopPropagation()}>
        <div className="adv-rm-topglow" />
        <span className="adv-cm-corner tl" /><span className="adv-cm-corner tr" />
        <span className="adv-cm-corner bl" /><span className="adv-cm-corner br" />
        <div className="adv-rm-header">
          <div className="adv-rm-header-bg" />
          <div className="adv-rm-header-inner">
            <div className="adv-rm-header-icon-wrap">
              <div className="adv-rm-hicon-ring" />
              <FaIdCard className="adv-rm-hicon" />
            </div>
            <div className="adv-rm-header-eyebrow">✦ {t("modIdProofSub")} ✦</div>
            <h2 className="adv-rm-title">{t("modIdProofTitle")}</h2>
            <p className="adv-rm-name-tag">{name}</p>
          </div>
          <button className="adv-rm-close" onClick={onClose}><FaClose /></button>
        </div>
        <div className="adv-rm-rule"><span className="adv-rm-rule-ornament">❖</span></div>
        <div className="adv-idproof-body">
          {!path ? (
            <div className="adv-idproof-empty">
              <div className="adv-idproof-empty-icon">🪬</div>
              <p>{t("modNoIdProof")}</p>
            </div>
          ) : isImage ? (
            <div className="adv-idproof-img-frame">
              <div className="adv-idproof-img-glow" />
              <img src={src} alt="ID Proof" className="adv-idproof-img" />
            </div>
          ) : isPdf ? (
            <iframe src={src} title="ID Proof PDF" className="adv-idproof-pdf" />
          ) : (
            <div className="adv-idproof-empty">
              <a href={src} target="_blank" rel="noreferrer" className="adv-btn adv-btn-gold">Download File</a>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ═══════════════════════════════════════════════════
   EDIT DEVOTEE MODAL
═══════════════════════════════════════════════════ */
function EditDevoteeModal({ devotee, onClose, onSaved, showToast }) {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    name:    devotee.name || "",
    mobile:  devotee.mobile_number || "",
    email:   devotee.email || "",
    place:   devotee.place || "",
    address: devotee.address || "",
  });
  const [errors, setErrors]             = useState({});
  const [availability, setAvailability] = useState({ mobile: null, email: null });
  const [checking, setChecking]         = useState({ mobile: false, email: false });
  const [loading, setLoading]           = useState(false);

  const originalMobile = devotee.mobile_number;
  const originalEmail  = devotee.email || "";

  useEffect(() => {
    const newErrors = {};
    if (formData.name && !formData.name.trim()) newErrors.name = t("errName");
    if (formData.mobile.length > 0 && !/^\d{10}$/.test(formData.mobile)) {
      newErrors.mobile = t("errMobile");
    } else if (formData.mobile !== originalMobile && availability.mobile === false) {
      newErrors.mobile = t("errMobileTaken");
    }
    if (formData.email && formData.email !== originalEmail) {
      if (availability.email === false) newErrors.email = t("errEmailTaken");
    }
    if (formData.place && !formData.place.trim()) newErrors.place = t("errPlace");
    if (formData.address && !formData.address.trim()) newErrors.address = t("errAddress");
    setErrors(newErrors);
  }, [formData, availability, originalMobile, originalEmail, t]);

  const checkAvailability = async (field, value) => {
    if ((field === "mobile" && value === originalMobile) ||
        (field === "email"  && value === originalEmail)) {
      setAvailability(prev => ({ ...prev, [field]: true }));
      return;
    }
    if (!value) return;
    setChecking(prev => ({ ...prev, [field]: true }));
    try {
      const res  = await fetch(`${API}/api/check/${field}/${value}`);
      const data = await res.json();
      setAvailability(prev => ({ ...prev, [field]: data.available }));
    } catch { /* silently ignore */ }
    finally { setChecking(prev => ({ ...prev, [field]: false })); }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === "mobile" && value.length === 10) checkAvailability("mobile", value);
    if (name === "email"  && value.includes("@")) checkAvailability("email",  value);
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim())    newErrors.name    = t("errName");
    if (!/^\d{10}$/.test(formData.mobile)) newErrors.mobile = t("errMobile");
    if (!formData.place.trim())   newErrors.place   = t("errPlace");
    if (!formData.address.trim()) newErrors.address = t("errAddress");
    if (formData.mobile !== originalMobile && availability.mobile === false) newErrors.mobile = t("errMobileTaken");
    if (formData.email && formData.email !== originalEmail && availability.email === false) newErrors.email = t("errEmailTaken");
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const payload = {
        name:      formData.name.trim(),
        email:     formData.email.trim() || null,
        address:   formData.address.trim(),
        place:     formData.place.trim(),
        newMobile: formData.mobile !== originalMobile ? formData.mobile : undefined,
      };
      const res  = await fetch(`${API}/api/admin/devotees/${originalMobile}/edit`, {
        method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Update failed");
      showToast(`${formData.name} has been updated successfully.`, "success");
      onSaved(data.mobile);
      onClose();
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const MobileHint = () => {
    if (formData.mobile === originalMobile || !formData.mobile || formData.mobile.length < 10) return null;
    if (checking.mobile) return <span className="adv-edit-hint checking">Checking…</span>;
    if (availability.mobile === true)  return <span className="adv-edit-hint ok">✓ Available</span>;
    if (availability.mobile === false) return <span className="adv-edit-hint taken">✗ Already taken</span>;
    return null;
  };
  const EmailHint = () => {
    if (!formData.email || formData.email === originalEmail || !formData.email.includes("@")) return null;
    if (checking.email) return <span className="adv-edit-hint checking">Checking…</span>;
    if (availability.email === true)  return <span className="adv-edit-hint ok">✓ Available</span>;
    if (availability.email === false) return <span className="adv-edit-hint taken">✗ Already taken</span>;
    return null;
  };

  return createPortal(
    <div className="adv-modal-overlay" onClick={onClose}>
      <div className="adv-regal-modal adv-edit-regal" onClick={e => e.stopPropagation()}>
        <div className="adv-rm-topglow" />
        <span className="adv-cm-corner tl" /><span className="adv-cm-corner tr" />
        <span className="adv-cm-corner bl" /><span className="adv-cm-corner br" />
        <div className="adv-rm-header">
          <div className="adv-rm-header-bg" />
          <div className="adv-rm-header-inner">
            <div className="adv-rm-header-icon-wrap">
              <div className="adv-rm-hicon-ring" />
              <FaEdit className="adv-rm-hicon" />
            </div>
            <div className="adv-rm-header-eyebrow">✦ {t("modEditEyebrow")} ✦</div>
            <h2 className="adv-rm-title">{t("modEditTitle")}</h2>
            <p className="adv-rm-name-tag">{devotee.name}</p>
          </div>
          <button className="adv-rm-close" onClick={onClose}><FaClose /></button>
        </div>
        <div className="adv-rm-rule"><span className="adv-rm-rule-ornament">❖</span></div>
        <div className="adv-edit-body">
          <div className="adv-edit-section-label">✦ {t("modEditSecInfo")}</div>
          <div className="adv-edit-grid">
            <div className="adv-edit-field full-width">
              <label className="adv-edit-label">{t("modEditLblName")} <span className="adv-req">*</span></label>
              <div className={`adv-edit-wrap ${errors.name ? "err" : ""}`}>
                <input type="text" name="name" className="adv-edit-input" placeholder={t("modEditPhName")} value={formData.name} onChange={handleChange} />
              </div>
              {errors.name && <span className="adv-edit-err">{errors.name}</span>}
            </div>
            <div className="adv-edit-field">
              <label className="adv-edit-label">{t("modEditLblMobile")} <span className="adv-req">*</span></label>
              <div className={`adv-edit-wrap ${errors.mobile ? "err" : ""}`}>
                <span className="adv-edit-prefix">🇮🇳 +91</span>
                <div className="adv-edit-sep" />
                <input type="tel" name="mobile" className="adv-edit-input" placeholder={t("modEditPhMobile")} maxLength="10" value={formData.mobile} onChange={handleChange} />
              </div>
              <MobileHint />
              {errors.mobile && <span className="adv-edit-err">{errors.mobile}</span>}
            </div>
            <div className="adv-edit-field">
              <label className="adv-edit-label">{t("modEditLblEmail")}</label>
              <div className={`adv-edit-wrap ${errors.email ? "err" : ""}`}>
                <input type="email" name="email" className="adv-edit-input" placeholder={t("modEditPhEmail")} value={formData.email} onChange={handleChange} />
              </div>
              <EmailHint />
              {errors.email && <span className="adv-edit-err">{errors.email}</span>}
            </div>
            <div className="adv-edit-field">
              <label className="adv-edit-label">{t("modEditLblPlace")} <span className="adv-req">*</span></label>
              <div className={`adv-edit-wrap ${errors.place ? "err" : ""}`}>
                <input type="text" name="place" className="adv-edit-input" placeholder={t("modEditPhPlace")} value={formData.place} onChange={handleChange} />
              </div>
              {errors.place && <span className="adv-edit-err">{errors.place}</span>}
            </div>
            <div className="adv-edit-field full-width">
              <label className="adv-edit-label">{t("modEditLblAddress")} <span className="adv-req">*</span></label>
              <div className={`adv-edit-wrap adv-edit-wrap-textarea ${errors.address ? "err" : ""}`}>
                <textarea name="address" className="adv-edit-textarea" placeholder={t("modEditPhAddress")} rows={3} value={formData.address} onChange={handleChange} />
              </div>
              {errors.address && <span className="adv-edit-err">{errors.address}</span>}
            </div>
          </div>
          <div className="adv-edit-actions" style={{ justifyContent: "center" }}>
            <button className="adv-btn adv-btn-ghost" onClick={onClose} disabled={loading}>{t("btnCancel")}</button>
            <button className="adv-edit-save-btn" onClick={handleSubmit} disabled={loading || Object.keys(errors).length > 0}>
              {loading ? <><span className="adv-edit-saving-spinner" />{t("modEditSaving")}…</> : <><FaSave /> {t("modEditSaveBtn")}</>}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ═══════════════════════════════════════════════════
   DEVOTEE DETAIL MODAL
═══════════════════════════════════════════════════ */
function DevoteeModal({ devotee, onClose, onApprove, onReject, onDeactivate, onDelete, onEdit, actionLoading }) {
  const { t } = useTranslation();
  const [showIdProof, setShowIdProof] = useState(false);
  if (!devotee) return null;

  const statusConfig = {
    approved: { color: "#2a6b2a", bg: "rgba(42,107,42,0.1)", label: t("statsApproved"), glyph: "✓" },
    pending:  { color: "#c9973a", bg: "rgba(201,151,58,0.1)", label: t("statsPending"),  glyph: "◌" },
    rejected: { color: "#8b1a1a", bg: "rgba(139,26,26,0.1)", label: t("statsRejected"), glyph: "✗" },
  };
  const sc = statusConfig[devotee.status] || statusConfig.pending;

  return createPortal(
    <>
      <div className="adv-modal-overlay" onClick={onClose}>
        <div className="adv-regal-modal adv-detail-regal" onClick={e => e.stopPropagation()}>
          <div className="adv-rm-topglow" />
          <span className="adv-cm-corner tl" /><span className="adv-cm-corner tr" />
          <span className="adv-cm-corner bl" /><span className="adv-cm-corner br" />
          <div className="adv-rm-header">
            <div className="adv-rm-header-bg" />
            <div className="adv-rm-header-inner">
              <div className="adv-rm-avatar-wrap">
                <div className="adv-rm-avatar-ring r1" />
                <div className="adv-rm-avatar-ring r2" />
                <div className="adv-rm-avatar-ring r3" />
                <div className="adv-rm-avatar-core">
                  <span>{devotee.name?.[0]?.toUpperCase() || "D"}</span>
                </div>
              </div>
              <div className="adv-rm-header-eyebrow">✦ {t("modDevoteeEyebrow")} ✦</div>
              <h2 className="adv-rm-title">{devotee.name}</h2>
              <p className="adv-rm-name-tag">+91 {devotee.mobile_number}</p>
              <div className="adv-rm-status-pill" style={{ color: sc.color, background: sc.bg, borderColor: `${sc.color}44` }}>
                <span>{sc.glyph}</span> {sc.label}
              </div>
            </div>
            <button className="adv-rm-close" onClick={onClose}><FaClose /></button>
          </div>
          <div className="adv-rm-rule"><span className="adv-rm-rule-ornament">❖</span></div>
          <div className="adv-detail-scroll-body">
            <div className="adv-detail-section-label">✦ {t("modDevoteeSecInfo")}</div>
            <div className="adv-detail-info-grid">
              {[
                { icon: <FaPhone />, label: t("lblMobile"), val: `+91 ${devotee.mobile_number}` },
                { icon: <FaEnvelope />, label: t("lblEmail"), val: devotee.email || "—" },
                { icon: <FaMapMarkerAlt />, label: t("lblPlace"), val: devotee.place || "—" },
                { icon: <FaCalendarAlt />, label: t("lblRegistered"), val: devotee.created_at ? new Date(devotee.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—" },
                ...(devotee.approved_at ? [{ icon: <FaCheckCircle />, label: t("lblApprovedAt"), val: new Date(devotee.approved_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }), accent: "#2a6b2a" }] : []),
              ].map((f, i) => (
                <div key={i} className="adv-di-field">
                  <div className="adv-di-icon" style={f.accent ? { color: f.accent } : {}}>{f.icon}</div>
                  <div>
                    <div className="adv-di-label">{f.label}</div>
                    <div className="adv-di-val">{f.val}</div>
                  </div>
                </div>
              ))}
            </div>
            {devotee.address && (
              <div className="adv-di-address-box">
                <div>
                  <div className="adv-di-address-label"><FaMapMarkerAlt /> {t("lblAddress")}</div>
                  <div className="adv-di-address-val">{devotee.address}</div>
                </div>
              </div>
            )}
            <button className="adv-idproof-launch-btn" onClick={() => setShowIdProof(true)}>
              <div className="adv-idproof-launch-icon"><FaIdCard /></div>
              <div>
                <div className="adv-idproof-launch-title">{t("btnViewId")}</div>
                <div className="adv-idproof-launch-sub">{t("btnOpenId")}</div>
              </div>
              <div className="adv-idproof-launch-arrow">→</div>
            </button>
            <div className="adv-detail-section-label" style={{ marginTop: 22 }}>✦ {t("secAdminActions")}</div>
            <div className="adv-detail-action-grid">
              <button className="adv-action-tile edit" onClick={() => onEdit(devotee)} disabled={actionLoading}>
                <FaEdit className="adv-at-icon" /><span className="adv-at-label">{t("modDevoteeTileEdit")}</span><span className="adv-at-desc">{t("modDevoteeTileEditDesc")}</span>
              </button>
              {devotee.status === "pending" && (
                <button className="adv-action-tile approve" onClick={() => onApprove(devotee)} disabled={actionLoading}>
                  <FaUserCheck className="adv-at-icon" /><span className="adv-at-label">{t("btnApprove")}</span><span className="adv-at-desc">{t("btnApproveDesc")}</span>
                </button>
              )}
              {devotee.status === "pending" && (
                <button className="adv-action-tile reject" onClick={() => onReject(devotee)} disabled={actionLoading}>
                  <FaTimes className="adv-at-icon" /><span className="adv-at-label">{t("btnReject")}</span><span className="adv-at-desc">{t("btnRejectDesc")}</span>
                </button>
              )}
              {devotee.status === "approved" && (
                <button className="adv-action-tile warn" onClick={() => onDeactivate(devotee)} disabled={actionLoading}>
                  <FaBan className="adv-at-icon" /><span className="adv-at-label">{t("btnDeactivate")}</span><span className="adv-at-desc">{t("btnDeactivateDesc")}</span>
                </button>
              )}
              {devotee.status === "rejected" && (
                <button className="adv-action-tile approve" onClick={() => onApprove(devotee)} disabled={actionLoading}>
                  <FaUserCheck className="adv-at-icon" /><span className="adv-at-label">{t("btnReApprove")}</span><span className="adv-at-desc">{t("btnReApproveDesc")}</span>
                </button>
              )}
              <button className="adv-action-tile danger" onClick={() => onDelete(devotee)} disabled={actionLoading}>
                <FaTrash className="adv-at-icon" /><span className="adv-at-label">{t("btnDelete")}</span><span className="adv-at-desc">{t("btnDeleteDesc")}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      {showIdProof && (
        <IdProofModal path={devotee.id_proof_path} name={devotee.name} onClose={() => setShowIdProof(false)} />
      )}
    </>,
    document.body
  );
}

/* ═══════════════════════════════════════════════════
   STATUS BADGE
═══════════════════════════════════════════════════ */
function StatusBadge({ status }) {
  const { t } = useTranslation();
  const map = {
    approved: { color: "#2a6b2a", bg: "rgba(42,107,42,0.1)", border: "rgba(42,107,42,0.25)", label: t("statsApproved") },
    pending:  { color: "#c9973a", bg: "rgba(201,151,58,0.1)", border: "rgba(201,151,58,0.25)", label: t("statsPending") },
    rejected: { color: "#8b1a1a", bg: "rgba(139,26,26,0.1)", border: "rgba(139,26,26,0.25)", label: t("statsRejected") },
  };
  const s = map[status] || map.pending;
  return (
    <span className="adv-status-badge" style={{ color: s.color, background: s.bg, border: `1px solid ${s.border}` }}>
      {s.label}
    </span>
  );
}

/* ═══════════════════════════════════════════════════
   STATUS DROPDOWN
═══════════════════════════════════════════════════ */
function StatusDropdown({ value, onChange }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const options = [
    { value: "all",      label: t("filterAll"),      color: "#c9973a" },
    { value: "pending",  label: t("filterPending"),  color: "#c9973a" },
    { value: "approved", label: t("filterApproved"), color: "#2a6b2a" },
    { value: "rejected", label: t("filterRejected"), color: "#8b1a1a" },
  ];

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selected = options.find(o => o.value === value) || options[0];

  return (
    <div className="adv-dropdown-wrap" ref={ref}>
      <button className="adv-dropdown-trigger" onClick={() => setOpen(o => !o)}>
        <span className="adv-dd-dot" style={{ background: selected.color }} />
        <span className="adv-dd-label">{selected.label}</span>
        <FaChevronDown className={`adv-dd-chevron ${open ? "open" : ""}`} />
      </button>
      {open && (
        <div className="adv-dropdown-menu">
          <div className="adv-dd-menu-header">{t("filterLabel")}</div>
          {options.map(o => (
            <button key={o.value} className={`adv-dd-option ${value === o.value ? "active" : ""}`} onClick={() => { onChange(o.value); setOpen(false); }}>
              <span className="adv-dd-opt-dot" style={{ background: o.color }} />
              <span>{o.label}</span>
              {value === o.value && <span className="adv-dd-check">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════ */
export default function ADevotees() {
  const { t, i18n } = useTranslation();

  // State
  const [devotees, setDevotees] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // UI States
  const [toast, setToast] = useState(null);
  const [selectedDevotee, setSelectedDevotee] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [editDevotee, setEditDevotee] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [logoFace, setLogoFace] = useState("om");
  const searchRef = useRef(null);

  useEffect(() => {
    const timer = setInterval(() => setLogoFace(f => f === "om" ? "logo" : "om"), 3000);
    return () => clearInterval(timer);
  }, []);

  const showToast = (message, type = "info") => setToast({ message, type });

  const fetchDevotees = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/devotees`);
      if (!res.ok) throw new Error("Failed to fetch");
      const data = await res.json();
      setDevotees(data);
      setFiltered(data);
    } catch (err) {
      showToast(t("advErrFetch"), "error");
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { fetchDevotees(); }, [fetchDevotees]);

  useEffect(() => {
    let result = [...devotees];
    if (statusFilter !== "all") result = result.filter(d => d.status === statusFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(d =>
        d.name?.toLowerCase().includes(q) ||
        d.mobile_number?.includes(q) ||
        d.email?.toLowerCase().includes(q) ||
        d.place?.toLowerCase().includes(q)
      );
    }
    setFiltered(result);
  }, [search, statusFilter, devotees]);

  const callAction = async (endpoint, method, body, successMsg) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API}${endpoint}`, {
        method, headers: { "Content-Type": "application/json" },
        body: body ? JSON.stringify(body) : undefined,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Action failed");
      showToast(successMsg, "success");
      setSelectedDevotee(null);
      setConfirmDialog(null);
      fetchDevotees();
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove    = (d) => setConfirmDialog({ title: t("modApproveTitle"), subtitle: t("modApproveSub"), message: `${t("modApproveMsg")} "${d.name}"?`, confirmLabel: t("modApproveBtn"), danger: false, icon: <FaUserCheck />, onConfirm: () => callAction(`/api/admin/devotees/${d.mobile_number}/status`, "PATCH", { status: "approved" }, `${d.name} has been approved.`) });
  const handleReject     = (d) => setConfirmDialog({ title: t("modRejectTitle"), subtitle: t("modRejectSub"), message: `${t("modRejectMsg")} "${d.name}"?`, confirmLabel: t("modRejectBtn"), danger: true, icon: <FaUserTimes />, onConfirm: () => callAction(`/api/admin/devotees/${d.mobile_number}/status`, "PATCH", { status: "rejected" }, `${d.name} has been rejected.`) });
  const handleDeactivate = (d) => setConfirmDialog({ title: t("modDeactivateTitle"), subtitle: t("modDeactivateSub"), message: `${t("modDeactivateMsg")} "${d.name}"?`, confirmLabel: t("modDeactivateBtn"), danger: true, icon: <FaBan />, onConfirm: () => callAction(`/api/admin/devotees/${d.mobile_number}/status`, "PATCH", { status: "rejected" }, `${d.name} has been deactivated.`) });
  const handleDelete     = (d) => setConfirmDialog({ title: t("modDeleteTitle"), subtitle: t("modDeleteSub"), message: `${t("modDeleteMsg")} "${d.name}"?`, confirmLabel: t("modDeleteBtn"), danger: true, icon: <FaTrash />, onConfirm: () => callAction(`/api/admin/devotees/${d.mobile_number}`, "DELETE", null, `${d.name} has been deleted.`) });

  const handleEdit      = (d) => { setSelectedDevotee(null); setEditDevotee(d); };
  const handleEditSaved = ()  => { fetchDevotees(); };

  const handleExport = () => {
    if (!filtered.length) { showToast("No data to export", "info"); return; }
    const rows = filtered.map(d => ({
      "Devotee ID": d.devotee_id, "Name": d.name, "Mobile": d.mobile_number,
      "Email": d.email || "", "Place": d.place || "", "Address": d.address || "",
      "Status": d.status,
      "Registered": d.created_at ? new Date(d.created_at).toLocaleDateString("en-IN") : "",
      "Approved At": d.approved_at ? new Date(d.approved_at).toLocaleDateString("en-IN") : "",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Devotees");
    XLSX.writeFile(wb, `Devotees_${new Date().toISOString().slice(0,10)}.xlsx`);
    showToast("Export successful!", "success");
  };

  const stats = {
    total:    devotees.length,
    approved: devotees.filter(d => d.status === "approved").length,
    pending:  devotees.filter(d => d.status === "pending").length,
    rejected: devotees.filter(d => d.status === "rejected").length,
  };

  return (
    <>
      <DevoteesStyles />

      {/* ── HERO — scrolls naturally inside .admin-content ── */}
      <div className="adv-hero">
        <div className="adv-hero-bg" />
        <div className="adv-hero-rings">
          {[220, 170, 125, 85].map((s, i) => (
            <div key={i} className="adv-hero-ring" style={{ width: s, height: s, animationDelay: `${i * 0.5}s` }} />
          ))}
        </div>
        <div className="adv-hero-medallion">
          <div className="adv-hero-med-ring" />
          <div className="adv-hero-med-ring adv-hero-med-ring-2" />
          <div className={`adv-hero-face ${logoFace === "om" ? "visible" : ""}`}>
            <span className="adv-hero-om">ॐ</span>
          </div>
          <div className={`adv-hero-face ${logoFace === "logo" ? "visible" : ""}`}>
            <img src="./logo.png" alt="Temple" className="adv-hero-logo-img" />
          </div>
          <div className="adv-hero-med-halo" />
        </div>
        <div className="adv-hero-text">
          <div className="adv-hero-eyebrow">✦ {t("templeName")} ✦</div>
          <h1 className="adv-hero-title">{t("heroTitle")}</h1>
          <p className="adv-hero-sub">{t("heroSub")}</p>
        </div>
        <div className="adv-hero-rule" />
        <div className="adv-stat-row">
          {[
            { label: t("statsTotal"),    val: stats.total,    color: "#c9973a", filter: "all" },
            { label: t("statsApproved"), val: stats.approved, color: "#2a6b2a", filter: "approved" },
            { label: t("statsPending"),  val: stats.pending,  color: "#c9973a", filter: "pending" },
            { label: t("statsRejected"), val: stats.rejected, color: "#8b1a1a", filter: "rejected" },
          ].map((s, i) => (
            <div key={i} className="adv-stat-card" style={{ "--scolor": s.color }} onClick={() => setStatusFilter(s.filter)}>
              <div className="adv-stat-val">{s.val}</div>
              <div className="adv-stat-label">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── TOOLBAR — sticky within the scroll container ── */}
      <div className="adv-toolbar">
        <div className="adv-search-wrap">
          <FaSearch className="adv-search-icon" />
          <input
            ref={searchRef}
            className="adv-search-input"
            placeholder={t("searchPlaceholder")}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && <button className="adv-search-clear" onClick={() => setSearch("")}><FaClose /></button>}
        </div>
        <StatusDropdown value={statusFilter} onChange={setStatusFilter} />
        <div className="adv-toolbar-btns">
          <button className="adv-action-btn adv-btn-refresh" onClick={fetchDevotees}>
            <FaSync className={loading ? "spin" : ""} /> {t("btnRefresh")}
          </button>
          <button className="adv-action-btn adv-btn-export" onClick={handleExport}>
            <FaFileExcel /> {t("btnExport")}
          </button>
        </div>
      </div>

      {/* ── RESULT INFO ── */}
      <div className="adv-result-info">
        {!loading && (
          <span>
            {t("showingCount")} <strong>{filtered.length}</strong> {t("ofCount")} <strong>{devotees.length}</strong> {t("devoteesCount")}
            {search && <> {t("noResultsSearch")} "<em>{search}</em>"</>}
          </span>
        )}
      </div>

      {/* ── TABLE ── */}
      <div className="adv-table-wrap">
        {loading ? (
          <OmLoader />
        ) : filtered.length === 0 ? (
          <div className="adv-empty">
            <div className="adv-empty-om">ॐ</div>
            <h3 className="adv-empty-title">{t("noResultsTitle")}</h3>
            <p className="adv-empty-sub">
              {search ? `${t("noResultsSearch")} "${search}". ${t("noResultsFilter")}` : t("noResultsFilter")}
            </p>
            <button className="adv-btn adv-btn-ghost" onClick={() => { setSearch(""); setStatusFilter("all"); }}>
              {t("clearFilters")}
            </button>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <table className="adv-table adv-desktop-only">
              <thead>
                <tr>
                  <th>#</th><th>{t("colDevotee")}</th><th>{t("colMobile")}</th><th>{t("colPlace")}</th>
                  <th>{t("colAddress")}</th><th>{t("colRegistered")}</th><th>{t("colStatus")}</th>
                  <th>{t("colIdProof")}</th><th>{t("colActions")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((d, i) => (
                  <tr key={d.devotee_id} className="adv-table-row" style={{ animationDelay: `${i * 0.04}s` }}>
                    <td className="adv-td-num">{i + 1}</td>
                    <td>
                      <div className="adv-devotee-cell">
                        <div className="adv-devotee-avatar">{d.name?.[0]?.toUpperCase() || "D"}</div>
                        <div className="adv-devotee-info">
                          <div className="adv-devotee-name">{d.name}</div>
                          <div className="adv-devotee-email">{d.email || "—"}</div>
                        </div>
                      </div>
                    </td>
                    <td className="adv-td-mobile">+91 {d.mobile_number}</td>
                    <td>{d.place || "—"}</td>
                    <td className="adv-td-address">{d.address || "—"}</td>
                    <td className="adv-td-date">{d.created_at ? new Date(d.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</td>
                    <td><StatusBadge status={d.status} /></td>
                    <td>
                      {d.id_proof_path
                        ? <button className="adv-icon-btn adv-btn-idproof" onClick={() => setSelectedDevotee({ ...d, _showId: true })}><FaIdCard /></button>
                        : <span className="adv-no-proof">—</span>}
                    </td>
                    <td>
                      <div className="adv-action-cell">
                        <button className="adv-icon-btn adv-btn-view" title="View" onClick={() => setSelectedDevotee(d)}><FaEye /></button>
                        <button className="adv-icon-btn adv-btn-edit-sm" title="Edit" onClick={() => handleEdit(d)}><FaEdit /></button>
                        {d.status === "pending"  && <button className="adv-icon-btn adv-btn-approve-sm" title="Approve" onClick={() => handleApprove(d)}><FaUserCheck /></button>}
                        {d.status === "pending"  && <button className="adv-icon-btn adv-btn-reject-sm" title="Reject" onClick={() => handleReject(d)}><FaTimes /></button>}
                        {d.status === "approved" && <button className="adv-icon-btn adv-btn-deactivate-sm" title="Deactivate" onClick={() => handleDeactivate(d)}><FaBan /></button>}
                        {d.status === "rejected" && <button className="adv-icon-btn adv-btn-approve-sm" title="Re-Approve" onClick={() => handleApprove(d)}><FaUserCheck /></button>}
                        <button className="adv-icon-btn adv-btn-delete-sm" title="Delete" onClick={() => handleDelete(d)}><FaTrash /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Cards */}
            <div className="adv-mobile-only adv-card-list">
              {filtered.map((d, i) => (
                <div key={d.devotee_id} className="adv-mob-card" style={{ animationDelay: `${i * 0.05}s` }}>
                  <div className="adv-mob-card-header">
                    <div className="adv-mob-avatar">{d.name?.[0]?.toUpperCase() || "D"}</div>
                    <div className="adv-mob-card-info">
                      <div className="adv-mob-name">{d.name}</div>
                      <div className="adv-mob-mobile">+91 {d.mobile_number}</div>
                      {d.email && <div className="adv-mob-email">{d.email}</div>}
                    </div>
                    <StatusBadge status={d.status} />
                  </div>
                  <div className="adv-mob-meta">
                    {d.place    && <div className="adv-mob-meta-item"><FaMapMarkerAlt className="adv-mob-meta-icon" /><span>{d.place}</span></div>}
                    {d.address  && <div className="adv-mob-meta-item adv-mob-address-item"><FaMapMarkerAlt className="adv-mob-meta-icon" /><span>{d.address}</span></div>}
                    {d.created_at && <div className="adv-mob-meta-item"><FaCalendarAlt className="adv-mob-meta-icon" /><span>{new Date(d.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</span></div>}
                  </div>
                  <div className="adv-mob-actions">
                    <button className="adv-mob-btn mob-view"    onClick={() => setSelectedDevotee(d)}><FaEye /> View</button>
                    <button className="adv-mob-btn mob-edit"    onClick={() => handleEdit(d)}><FaEdit /> Edit</button>
                    {d.id_proof_path && <button className="adv-mob-btn mob-idproof" onClick={() => setSelectedDevotee({ ...d, _showId: true })}><FaIdCard /> ID</button>}
                    {d.status === "pending"  && <button className="adv-mob-btn mob-approve" onClick={() => handleApprove(d)}><FaUserCheck /> Approve</button>}
                    {d.status === "pending"  && <button className="adv-mob-btn mob-reject"  onClick={() => handleReject(d)}><FaTimes /> Reject</button>}
                    {d.status === "approved" && <button className="adv-mob-btn mob-warn"    onClick={() => handleDeactivate(d)}><FaBan /> Deactivate</button>}
                    {d.status === "rejected" && <button className="adv-mob-btn mob-approve" onClick={() => handleApprove(d)}><FaUserCheck /> Re-Approve</button>}
                    <button className="adv-mob-btn mob-delete"  onClick={() => handleDelete(d)}><FaTrash /></button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* ── MODALS — all use position:fixed, always centered on viewport ── */}
      {selectedDevotee && !selectedDevotee._showId && (
        <DevoteeModal
          devotee={selectedDevotee}
          onClose={() => setSelectedDevotee(null)}
          onApprove={(d)    => { setSelectedDevotee(null); handleApprove(d); }}
          onReject={(d)     => { setSelectedDevotee(null); handleReject(d); }}
          onDeactivate={(d) => { setSelectedDevotee(null); handleDeactivate(d); }}
          onDelete={(d)     => { setSelectedDevotee(null); handleDelete(d); }}
          onEdit={handleEdit}
          actionLoading={actionLoading}
        />
      )}
      {selectedDevotee?._showId && (
        <IdProofModal path={selectedDevotee.id_proof_path} name={selectedDevotee.name} onClose={() => setSelectedDevotee(null)} />
      )}
      {editDevotee && (
        <EditDevoteeModal
          devotee={editDevotee}
          onClose={() => setEditDevotee(null)}
          onSaved={handleEditSaved}
          showToast={showToast}
        />
      )}
      {confirmDialog && (
        <ConfirmDialog
          title={confirmDialog.title}
          subtitle={confirmDialog.subtitle}
          message={confirmDialog.message}
          confirmLabel={confirmDialog.confirmLabel}
          danger={confirmDialog.danger}
          icon={confirmDialog.icon}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog(null)}
        />
      )}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </>
  );
}

/* ═══════════════════════════════════════════════════
   STYLES
═══════════════════════════════════════════════════ */
function DevoteesStyles() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Cinzel+Decorative:wght@400;700;900&family=Cinzel:wght@400;500;600;700&family=EB+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&display=swap');

      :root {
        --gold-bright: #e8c46a; --gold-mid: #c9973a; --gold-dim: #8a6020;
        --crimson-deep: #1a0404; --crimson-mid: #2c0808; --crimson-rich: #3d0c0c;
        --hero-1: #2e0c0c; --hero-2: #1c0505; --hero-3: #100202;
        --parchment: #FDF3E3; --parchment-dark: #f5e8cc;
        --text-dark: #1a0404; --text-mid: #4a1a08;
      }

      /* ══ HERO ══ */
      .adv-hero { position: relative; overflow: hidden; background: linear-gradient(160deg, var(--hero-1) 0%, var(--hero-2) 50%, var(--hero-3) 100%); padding: 40px 32px 0; display: flex; flex-direction: column; align-items: center; border-radius: 16px 16px 0 0; }
      .adv-hero-bg { position: absolute; inset: 0; pointer-events: none; background: radial-gradient(ellipse at 15% 30%, rgba(160,35,35,0.35) 0%, transparent 50%), radial-gradient(ellipse at 85% 70%, rgba(80,15,15,0.3) 0%, transparent 45%); }
      .adv-hero-rings { position: absolute; top: 30px; right: 60px; width: 230px; height: 230px; pointer-events: none; }
      .adv-hero-ring { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); border-radius: 50%; border: 1px solid rgba(232,196,106,0.07); animation: heroRingPulse 4s ease-in-out infinite alternate; }
      .adv-hero-ring:nth-child(odd) { border-style: dashed; }
      @keyframes heroRingPulse { from { opacity: 0.3; } to { opacity: 0.8; } }
      .adv-hero-medallion { width: 90px; height: 90px; border-radius: 50%; background: radial-gradient(circle at 40% 35%, #5a1515 0%, #2a0707 55%, #0e0101 100%); border: 1px solid rgba(232,196,106,0.3); display: flex; align-items: center; justify-content: center; position: relative; margin-bottom: 20px; box-shadow: 0 0 30px rgba(232,196,106,0.15), inset 0 1px 0 rgba(255,248,231,0.06); }
      .adv-hero-med-ring { position: absolute; inset: -5px; border-radius: 50%; border: 1px dashed rgba(232,196,106,0.25); animation: slowSpin 10s linear infinite; }
      .adv-hero-med-ring-2 { inset: -10px; border-style: dotted; border-color: rgba(232,196,106,0.12); animation-duration: 18s; animation-direction: reverse; }
      @keyframes slowSpin { to { transform: rotate(360deg); } }
      .adv-hero-face { position: absolute; inset: 0; border-radius: 50%; display: flex; align-items: center; justify-content: center; opacity: 0; transform: scale(0.7) rotateY(90deg); transition: opacity 0.55s ease, transform 0.55s ease; }
      .adv-hero-face.visible { opacity: 1; transform: scale(1) rotateY(0deg); }
      .adv-hero-om { font-family: 'Cinzel Decorative', cursive; font-size: 34px; background: linear-gradient(180deg, #fff8e7, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; filter: drop-shadow(0 0 8px rgba(232,196,106,0.5)); }
      .adv-hero-logo-img { width: 54px; height: 54px; object-fit: contain; filter: drop-shadow(0 0 8px rgba(232,196,106,0.4)); }
      .adv-hero-med-halo { position: absolute; inset: -16px; border-radius: 50%; background: radial-gradient(circle, rgba(232,196,106,0.08) 0%, transparent 65%); pointer-events: none; }
      .adv-hero-text { text-align: center; position: relative; z-index: 1; }
      .adv-hero-eyebrow { font-family: 'Cinzel', serif; font-size: 8.5px; letter-spacing: 2px; color: rgba(232,196,106,0.35); text-transform: uppercase; margin-bottom: 10px; }
      .adv-hero-title { font-family: 'Cinzel Decorative', cursive; font-size: 28px; font-weight: 900; background: linear-gradient(135deg, #fffaf0, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; line-height: 1.2; margin-bottom: 8px; }
      .adv-hero-sub { font-family: 'EB Garamond', serif; font-style: italic; font-size: 15px; color: rgba(245,228,176,0.4); margin-bottom: 20px; }
      .adv-hero-rule { width: 60%; max-width: 400px; height: 1px; margin: 0 auto 24px; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.4), transparent); }
      .adv-stat-row { display: flex; gap: 14px; flex-wrap: wrap; justify-content: center; padding: 0 0 28px; position: relative; z-index: 1; }
      .adv-stat-card { background: rgba(255,255,255,0.04); border: 1px solid rgba(201,151,58,0.18); padding: 14px 22px; text-align: center; cursor: pointer; clip-path: polygon(10px 0%, 100% 0%, calc(100% - 10px) 100%, 0% 100%); transition: background 0.2s, border-color 0.2s; min-width: 90px; }
      .adv-stat-card:hover { background: rgba(201,151,58,0.08); border-color: rgba(201,151,58,0.35); }
      .adv-stat-val { font-family: 'Cinzel Decorative', cursive; font-size: 22px; font-weight: 900; color: var(--scolor, var(--gold-bright)); }
      .adv-stat-label { font-family: 'Cinzel', serif; font-size: 8px; letter-spacing: 1.5px; text-transform: uppercase; color: rgba(245,228,176,0.4); margin-top: 3px; }

      /* ══ TOOLBAR — sticky inside scroll container ══ */
      .adv-toolbar {
        display: flex; flex-wrap: wrap; gap: 12px; align-items: center;
        padding: 20px 28px 12px;
        background: var(--parchment);
        border-bottom: 1px solid rgba(201,151,58,0.15);
        /* Sticky within .admin-content scroll area */
        position: sticky;
        top: 0;
        z-index: 10;
        box-shadow: 0 4px 12px rgba(26,4,4,0.06);
      }
      .adv-search-wrap { flex: 1; min-width: 220px; display: flex; align-items: center; background: #fff9f0; border: 1px solid rgba(201,151,58,0.28); clip-path: polygon(10px 0%, calc(100% - 10px) 0%, 100% 50%, calc(100% - 10px) 100%, 10px 100%, 0% 50%); overflow: hidden; transition: border-color 0.2s, box-shadow 0.2s; }
      .adv-search-wrap:focus-within { border-color: var(--gold-mid); box-shadow: 0 0 0 3px rgba(201,151,58,0.1); }
      .adv-search-icon { margin: 0 10px 0 18px; color: var(--gold-dim); font-size: 13px; flex-shrink: 0; }
      .adv-search-input { flex: 1; border: none; outline: none; background: transparent; font-family: 'EB Garamond', serif; font-size: 15px; color: var(--text-dark); padding: 11px 8px; }
      .adv-search-input::placeholder { color: rgba(74,26,8,0.28); font-style: italic; }
      .adv-search-clear { background: none; border: none; cursor: pointer; padding: 0 14px; color: rgba(74,26,8,0.35); font-size: 11px; transition: color 0.2s; }
      .adv-search-clear:hover { color: #8b1a1a; }

      /* ══ DROPDOWN ══ */
      .adv-dropdown-wrap { position: relative; flex-shrink: 0; }
      .adv-dropdown-trigger { display: flex; align-items: center; gap: 9px; font-family: 'Cinzel', serif; font-size: 9.5px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; background: linear-gradient(135deg, var(--hero-1), var(--hero-2)); border: 1px solid rgba(201,151,58,0.4); color: var(--gold-bright); padding: 10px 16px; cursor: pointer; clip-path: polygon(10px 0%, calc(100% - 10px) 0%, 100% 50%, calc(100% - 10px) 100%, 10px 100%, 0% 50%); transition: all 0.2s; min-width: 180px; }
      .adv-dropdown-trigger:hover { border-color: var(--gold-mid); background: linear-gradient(135deg, #3d1010, #2a0808); }
      .adv-dd-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; box-shadow: 0 0 6px currentColor; }
      .adv-dd-label { flex: 1; }
      .adv-dd-chevron { font-size: 9px; color: rgba(232,196,106,0.5); transition: transform 0.25s; }
      .adv-dd-chevron.open { transform: rotate(180deg); }
      .adv-dropdown-menu { position: absolute; top: calc(100% + 8px); left: 50%; transform: translateX(-50%); min-width: 220px; z-index: 500; background: linear-gradient(160deg, #2a0909, #1a0404, #0e0101); border: 1px solid rgba(201,151,58,0.35); clip-path: polygon(0 8px,8px 0,calc(100% - 8px) 0,100% 8px,100% calc(100% - 8px),calc(100% - 8px) 100%,8px 100%,0 calc(100% - 8px)); box-shadow: 0 16px 48px rgba(26,4,4,0.6); overflow: hidden; animation: ddIn 0.2s cubic-bezier(0.23,1,0.32,1) both; }
      @keyframes ddIn { from { opacity: 0; transform: translateX(-50%) translateY(-8px) scale(0.97); } to { opacity: 1; transform: translateX(-50%) translateY(0) scale(1); } }
      .adv-dd-menu-header { font-family: 'Cinzel', serif; font-size: 7.5px; font-weight: 700; letter-spacing: 2.5px; text-transform: uppercase; color: rgba(232,196,106,0.3); padding: 10px 16px 6px; border-bottom: 1px solid rgba(201,151,58,0.12); }
      .adv-dd-option { width: 100%; display: flex; align-items: center; gap: 10px; font-family: 'Cinzel', serif; font-size: 9.5px; font-weight: 600; letter-spacing: 1px; text-transform: uppercase; background: none; border: none; color: rgba(232,196,106,0.55); padding: 10px 16px; cursor: pointer; transition: all 0.18s; text-align: left; }
      .adv-dd-option:hover { background: rgba(201,151,58,0.07); color: var(--gold-bright); }
      .adv-dd-option.active { background: rgba(201,151,58,0.1); color: var(--gold-bright); }
      .adv-dd-opt-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
      .adv-dd-check { margin-left: auto; color: var(--gold-mid); font-size: 12px; }

      .adv-toolbar-btns { display: flex; gap: 8px; margin-left: auto; }
      .adv-action-btn { display: flex; align-items: center; gap: 7px; font-family: 'Cinzel', serif; font-size: 9.5px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; border: none; padding: 10px 18px; cursor: pointer; clip-path: polygon(10px 0%, calc(100% - 10px) 0%, 100% 50%, calc(100% - 10px) 100%, 10px 100%, 0% 50%); transition: all 0.25s; }
      .adv-btn-refresh { background: rgba(201,151,58,0.1); border: 1px solid rgba(201,151,58,0.3); color: var(--gold-dim); }
      .adv-btn-refresh:hover { background: rgba(201,151,58,0.18); }
      .adv-btn-export { background: linear-gradient(135deg, #1a4d1a, #2a6b2a); color: #c8f0c8; border: 1px solid rgba(42,107,42,0.4); }
      .adv-btn-export:hover { filter: brightness(1.1); }
      .spin { animation: spinIcon 0.8s linear infinite; }
      @keyframes spinIcon { to { transform: rotate(360deg); } }

      .adv-result-info { padding: 6px 28px; font-family: 'EB Garamond', serif; font-style: italic; font-size: 13px; color: rgba(74,26,8,0.45); background: var(--parchment); }

      /* ══ TABLE ══ */
      .adv-table-wrap { padding: 0 28px 40px; background: var(--parchment); overflow-x: auto; }
      .adv-table { width: 100%; border-collapse: separate; border-spacing: 0 6px; font-family: 'EB Garamond', serif; }
      .adv-table thead tr { background: linear-gradient(135deg, var(--hero-1) 0%, var(--hero-2) 55%, var(--hero-3) 100%); }
      .adv-table thead tr th { font-family: 'Cinzel', serif; font-size: 8.5px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--gold-bright); padding: 14px 14px; text-align: left; border-bottom: 1px solid rgba(201,151,58,0.25); white-space: nowrap; position: relative; }
      .adv-table thead tr th:first-child { border-radius: 3px 0 0 3px; padding-left: 18px; }
      .adv-table thead tr th:last-child { border-radius: 0 3px 3px 0; }
      .adv-table thead tr th::after { content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 1px; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.4), transparent); }
      .adv-table-row { background: linear-gradient(135deg, #FDF3E3, #faf0dc); border: 1px solid rgba(201,151,58,0.15); animation: rowIn 0.35s ease both; transition: box-shadow 0.2s, transform 0.2s; }
      .adv-table-row:hover { box-shadow: 0 4px 18px rgba(26,4,4,0.1); transform: translateY(-1px); }
      @keyframes rowIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      .adv-table-row td { padding: 12px 14px; vertical-align: middle; font-size: 14px; color: var(--text-mid); border-top: 1px solid rgba(201,151,58,0.1); border-bottom: 1px solid rgba(201,151,58,0.1); }
      .adv-table-row td:first-child { border-left: 1px solid rgba(201,151,58,0.1); border-radius: 2px 0 0 2px; }
      .adv-table-row td:last-child  { border-right: 1px solid rgba(201,151,58,0.1); border-radius: 0 2px 2px 0; }
      .adv-td-num { font-family: 'Cinzel', serif; font-size: 10px; color: rgba(74,26,8,0.35); width: 36px; }
      .adv-td-mobile { font-family: 'Cinzel', serif; font-size: 11px; letter-spacing: 0.5px; }
      .adv-td-date { font-family: 'Cinzel', serif; font-size: 10px; color: rgba(74,26,8,0.5); white-space: nowrap; }
      .adv-td-address { font-family: 'EB Garamond', serif; font-size: 13px; color: rgba(74,26,8,0.6); max-width: 180px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      .adv-devotee-cell { display: flex; align-items: center; gap: 10px; }
      .adv-devotee-avatar { width: 34px; height: 34px; border-radius: 50%; flex-shrink: 0; background: radial-gradient(circle, var(--crimson-rich), var(--crimson-deep)); border: 1px solid rgba(232,196,106,0.25); display: flex; align-items: center; justify-content: center; font-family: 'Cinzel Decorative', cursive; font-size: 13px; color: var(--gold-bright); }
      .adv-devotee-name { font-family: 'Cinzel', serif; font-size: 11px; font-weight: 700; letter-spacing: 0.5px; color: var(--text-dark); }
      .adv-devotee-email { font-family: 'EB Garamond', serif; font-style: italic; font-size: 12px; color: rgba(74,26,8,0.45); }
      .adv-status-badge { font-family: 'Cinzel', serif; font-size: 8px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; padding: 4px 10px; border-radius: 1px; white-space: nowrap; }
      .adv-no-proof { color: rgba(74,26,8,0.25); font-style: italic; font-size: 12px; }
      .adv-action-cell { display: flex; gap: 5px; align-items: center; flex-wrap: wrap; }
      .adv-icon-btn { width: 30px; height: 30px; border: 1px solid; border-radius: 2px; display: flex; align-items: center; justify-content: center; font-size: 12px; cursor: pointer; background: none; transition: all 0.18s; flex-shrink: 0; }
      .adv-btn-view           { border-color: rgba(201,151,58,0.3); color: var(--gold-dim); }
      .adv-btn-view:hover     { background: rgba(201,151,58,0.12); color: var(--gold-mid); }
      .adv-btn-idproof        { border-color: rgba(74,100,140,0.3); color: #4a648c; }
      .adv-btn-idproof:hover  { background: rgba(74,100,140,0.1); }
      .adv-btn-edit-sm        { border-color: rgba(100,74,140,0.35); color: #6a4a8c; }
      .adv-btn-edit-sm:hover  { background: rgba(100,74,140,0.1); }
      .adv-btn-approve-sm       { border-color: rgba(42,107,42,0.3); color: #2a6b2a; }
      .adv-btn-approve-sm:hover { background: rgba(42,107,42,0.1); }
      .adv-btn-reject-sm        { border-color: rgba(139,26,26,0.3); color: #8b1a1a; }
      .adv-btn-reject-sm:hover  { background: rgba(139,26,26,0.1); }
      .adv-btn-deactivate-sm       { border-color: rgba(180,100,0,0.3); color: #b46400; }
      .adv-btn-deactivate-sm:hover { background: rgba(180,100,0,0.08); }
      .adv-btn-delete-sm        { border-color: rgba(139,26,26,0.25); color: rgba(139,26,26,0.7); }
      .adv-btn-delete-sm:hover  { background: rgba(139,26,26,0.08); color: #8b1a1a; }
      .adv-icon-btn:disabled { opacity: 0.35; cursor: not-allowed; }

      /* ══ LOADER ══ */
      .adv-loader-wrap { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 80px 0; gap: 14px; }
      .adv-loader-rings { position: relative; width: 100px; height: 100px; margin-bottom: 10px; }
      .adv-loader-ring { position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%); border-radius: 50%; border: 1px dashed rgba(201,151,58,0.3); animation: loaderPulse 2s ease-in-out infinite; }
      .adv-loader-ring:nth-child(2) { border-style: solid; border-color: rgba(201,151,58,0.15); }
      .adv-loader-ring:nth-child(3) { animation: slowSpin 3s linear infinite; border-color: rgba(201,151,58,0.25); }
      @keyframes loaderPulse { 0%,100% { opacity: 0.3; transform: translate(-50%,-50%) scale(0.95); } 50% { opacity: 1; transform: translate(-50%,-50%) scale(1.02); } }
      .adv-loader-om { position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%); font-family: 'Cinzel Decorative', cursive; font-size: 28px; background: linear-gradient(180deg, #fff8e7, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; animation: omGlow 2s ease-in-out infinite alternate; }
      @keyframes omGlow { from { filter: drop-shadow(0 0 4px rgba(232,196,106,0.3)); } to { filter: drop-shadow(0 0 12px rgba(232,196,106,0.7)); } }
      .adv-loader-text { font-family: 'EB Garamond', serif; font-style: italic; font-size: 15px; color: rgba(74,26,8,0.4); }

      /* ══ EMPTY ══ */
      .adv-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 70px 0; gap: 14px; text-align: center; }
      .adv-empty-om { font-family: 'Cinzel Decorative', cursive; font-size: 52px; background: linear-gradient(180deg, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; opacity: 0.25; }
      .adv-empty-title { font-family: 'Cinzel Decorative', cursive; font-size: 20px; background: linear-gradient(135deg, var(--crimson-rich), var(--crimson-deep)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
      .adv-empty-sub { font-family: 'EB Garamond', serif; font-style: italic; font-size: 15px; color: rgba(74,26,8,0.45); max-width: 320px; }

      /* ══ BUTTONS ══ */
      .adv-btn { display: inline-flex; align-items: center; gap: 7px; font-family: 'Cinzel', serif; font-size: 9.5px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; border: none; padding: 10px 18px; cursor: pointer; clip-path: polygon(10px 0%,calc(100% - 10px) 0%,100% 50%,calc(100% - 10px) 100%,10px 100%,0 50%); transition: all 0.2s; border: 1px solid transparent; }
      .adv-btn:disabled { opacity: 0.38; cursor: not-allowed; }
      .adv-btn-gold { background: linear-gradient(135deg,#7a4e0c,var(--gold-mid),var(--gold-bright),#fff8e7,var(--gold-bright),var(--gold-mid),#6b4209); color: var(--crimson-deep); }
      .adv-btn-ghost { background: none; font-weight:bold; border-color: rgba(149, 27, 27, 0.5); color: rgb(74, 26, 8); }
      .adv-btn-ghost:hover { border-color: rgba(149, 27, 27, 0.5); background: rgba(201,151,58,0.06); }

      /* ══ MODAL OVERLAY — always fixed to viewport ══ */
      .adv-modal-overlay {
        position: fixed;    /* KEY: fixed, not absolute */
        inset: 0;
        z-index: 1000;
        background: rgba(8,1,1,0.85);
        backdrop-filter: blur(7px);
        display: flex; align-items: center; justify-content: center;
        padding: 20px;
        animation: overlayIn 0.2s ease;
      }
      @keyframes overlayIn { from { opacity: 0; } to { opacity: 1; } }

      /* ══ REGAL MODAL BASE ══ */
      .adv-regal-modal { position: relative; width: 100%; background: linear-gradient(160deg, #FDF3E3 0%, #faf0dc 55%, #f5e8cc 100%); border: 1px solid rgba(201,151,58,0.35);  box-shadow: 0 36px 110px rgba(26,4,4,0.7), 0 0 0 1px rgba(201,151,58,0.06) inset; animation: regalIn 0.35s cubic-bezier(0.23,1,0.32,1) both; overflow: hidden; }
      @keyframes regalIn { from { opacity: 0; transform: scale(0.93) translateY(26px); } to { opacity: 1; transform: scale(1) translateY(0); } }
      .adv-detail-regal { max-width: 540px; max-height: 90vh; overflow-y: auto; }
      .adv-detail-regal::-webkit-scrollbar { width: 6px; }
      .adv-detail-regal::-webkit-scrollbar-track { background: linear-gradient(180deg, #f5e8cc, #faf0dc); border-radius: 10px; }
      .adv-detail-regal::-webkit-scrollbar-thumb { background: linear-gradient(180deg, var(--hero-1), #5a1515, var(--gold-mid)); border-radius: 10px; border: 1px solid rgba(232,196,106,0.3); box-shadow: 0 0 6px rgba(232,196,106,0.25); }
      .adv-idproof-regal { max-width: 700px; max-height: 90vh; display: flex; flex-direction: column; }

      .adv-edit-regal { max-width: 580px; max-height: 92vh; overflow-y: auto; }
      .adv-edit-regal::-webkit-scrollbar { width: 6px; }
      .adv-edit-regal::-webkit-scrollbar-track { background: linear-gradient(180deg, #f5e8cc, #faf0dc); border-radius: 10px; }
      .adv-edit-regal::-webkit-scrollbar-thumb { background: linear-gradient(180deg, var(--hero-1), #5a1515, var(--gold-mid)); border-radius: 10px; border: 1px solid rgba(232,196,106,0.3); }

      .adv-rm-topglow { position: absolute; top: 0; left: 0; right: 0; height: 3px; background: linear-gradient(90deg, transparent 0%, rgba(201,151,58,0.2) 20%, rgba(232,196,106,0.9) 50%, rgba(201,151,58,0.2) 80%, transparent 100%); z-index: 10; }
      .adv-cm-corner { position: absolute; width: 16px; height: 16px; border-color: rgba(201,151,58,0.5); border-style: solid; z-index: 10; }
      .adv-cm-corner.tl { top: 14px; left: 14px; border-width: 1.5px 0 0 1.5px; }
      .adv-cm-corner.tr { top: 14px; right: 14px; border-width: 1.5px 1.5px 0 0; }
      .adv-cm-corner.bl { bottom: 14px; left: 14px; border-width: 0 0 1.5px 1.5px; }
      .adv-cm-corner.br { bottom: 14px; right: 14px; border-width: 0 1.5px 1.5px 0; }

      .adv-rm-header { position: relative; overflow: hidden; background: linear-gradient(160deg, var(--hero-1), var(--hero-2) 55%, var(--hero-3)); padding: 38px 28px 28px; text-align: center; border-bottom: 1px solid rgba(201,151,58,0.2); }
      .adv-rm-header-bg { position: absolute; inset: 0; background: radial-gradient(ellipse at 50% 40%, rgba(150,30,30,0.28) 0%, transparent 65%); pointer-events: none; }
      .adv-rm-header-inner { position: relative; z-index: 1; display: flex; flex-direction: column; align-items: center; gap: 8px; }
      .adv-rm-close { position: absolute; top: 16px; right: 16px; z-index: 5; background: rgba(255,255,255,0.06); border: 1px solid rgba(232,196,106,0.2); color: rgba(232,196,106,0.55); border-radius: 50%; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 11px; transition: all 0.2s; }
      .adv-rm-close:hover { background: rgba(255,255,255,0.12); color: var(--gold-bright); transform: rotate(90deg); }
      .adv-rm-avatar-wrap { position: relative; width: 80px; height: 80px; margin-bottom: 4px; }
      .adv-rm-avatar-ring { position: absolute; border-radius: 50%; border: 1px dashed; animation: slowSpin 8s linear infinite; }
      .adv-rm-avatar-ring.r1 { inset: -6px; border-color: rgba(232,196,106,0.22); }
      .adv-rm-avatar-ring.r2 { inset: -12px; border-color: rgba(232,196,106,0.1); animation-duration: 14s; animation-direction: reverse; border-style: dotted; }
      .adv-rm-avatar-ring.r3 { inset: -20px; border-color: rgba(232,196,106,0.05); animation-duration: 22s; }
      .adv-rm-avatar-core { width: 80px; height: 80px; border-radius: 50%; background: radial-gradient(circle at 35% 30%, #5a1515, #2a0707 60%, #0e0101); border: 2px solid rgba(232,196,106,0.35); display: flex; align-items: center; justify-content: center; box-shadow: 0 0 28px rgba(232,196,106,0.18); }
      .adv-rm-avatar-core span { font-family: 'Cinzel Decorative', cursive; font-size: 28px; background: linear-gradient(135deg, #fffaf0, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
      .adv-rm-header-icon-wrap { position: relative; width: 72px; height: 72px; border-radius: 50%; display: flex; align-items: center; justify-content: center; background: rgba(232,196,106,0.08); border: 1px solid rgba(232,196,106,0.2); }
      .adv-rm-hicon-ring { position: absolute; inset: -5px; border-radius: 50%; border: 1px dashed rgba(232,196,106,0.28); animation: slowSpin 9s linear infinite; }
      .adv-rm-hicon { font-size: 28px; color: var(--gold-bright); filter: drop-shadow(0 0 10px rgba(232,196,106,0.4)); position: relative; z-index: 1; }
      .adv-rm-header-eyebrow { font-family: 'Cinzel', serif; font-size: 7.5px; letter-spacing: 3px; color: rgba(232,196,106,0.3); text-transform: uppercase; }
      .adv-rm-title { font-family: 'Cinzel Decorative', cursive; font-size: 22px; font-weight: 900; background: linear-gradient(135deg, #fffaf0, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; line-height: 1.25; }
      .adv-rm-name-tag { font-family: 'Cinzel', serif; font-size: 11px; letter-spacing: 1px; color: rgba(245,228,176,0.4); }
      .adv-rm-status-pill { font-family: 'Cinzel', serif; font-size: 8.5px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; padding: 5px 16px; border: 1px solid; display: flex; align-items: center; gap: 6px; clip-path: polygon(8px 0%,calc(100% - 8px) 0%,100% 50%,calc(100% - 8px) 100%,8px 100%,0 50%); }
      .adv-rm-rule { display: flex; align-items: center; justify-content: center; height: 1px; margin: 0 40px; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.4), transparent); position: relative; z-index: 1; }
      .adv-rm-rule-ornament { position: absolute; font-size: 12px; color: var(--gold-mid); background: var(--parchment); padding: 0 8px; line-height: 1; }

      /* ══ DETAIL BODY ══ */
      .adv-detail-scroll-body { padding: 22px 28px 28px; position: relative; z-index: 1; }
      .adv-detail-section-label { font-family: 'Cinzel', serif; font-size: 8px; font-weight: 700; letter-spacing: 2.5px; text-transform: uppercase; color: var(--gold-dim); margin-bottom: 14px; padding-bottom: 7px; border-bottom: 1px solid rgba(201,151,58,0.15); }
      .adv-detail-info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 14px; }
      .adv-di-field { display: flex; align-items: flex-start; gap: 10px; padding: 10px 12px; background: rgba(201,151,58,0.04); border: 1px solid rgba(201,151,58,0.1); border-radius: 1px; transition: background 0.2s; }
      .adv-di-field:hover { background: rgba(201,151,58,0.08); }
      .adv-di-icon { color: var(--gold-dim); font-size: 13px; margin-top: 3px; flex-shrink: 0; }
      .adv-di-label { font-family: 'Cinzel', serif; font-size: 7.5px; letter-spacing: 1.5px; text-transform: uppercase; color: rgba(74,26,8,0.38); margin-bottom: 3px; }
      .adv-di-val { font-family: 'EB Garamond', serif; font-size: 15px; color: var(--text-mid); }
      .adv-di-address-box { background: rgba(201,151,58,0.05); border: 1px solid rgba(201,151,58,0.12); padding: 12px 14px; margin-bottom: 14px; }
      .adv-di-address-label { font-family: 'Cinzel', serif; font-size: 8px; letter-spacing: 1.5px; text-transform: uppercase; color: rgba(74,26,8,0.38); margin-bottom: 4px; display: flex; align-items: center; gap: 6px; }
      .adv-di-address-val { font-family: 'EB Garamond', serif; font-size: 15px; color: var(--text-mid); line-height: 1.5; }
      .adv-idproof-launch-btn { width: 100%; display: flex; align-items: center; gap: 14px; background: linear-gradient(135deg, rgba(74,100,140,0.07), rgba(74,100,140,0.03)); border: 1px solid rgba(74,100,140,0.22); padding: 12px 16px; cursor: pointer; margin-bottom: 4px; transition: all 0.2s; text-align: left; clip-path: polygon(8px 0%,calc(100% - 8px) 0%,100% 50%,calc(100% - 8px) 100%,8px 100%,0 50%); }
      .adv-idproof-launch-btn:hover { background: linear-gradient(135deg, rgba(74,100,140,0.14), rgba(74,100,140,0.07)); border-color: rgba(74,100,140,0.4); }
      .adv-idproof-launch-icon { width: 36px; height: 36px; border-radius: 50%; background: rgba(74,100,140,0.1); border: 1px solid rgba(74,100,140,0.25); display: flex; align-items: center; justify-content: center; font-size: 15px; color: #4a648c; flex-shrink: 0; }
      .adv-idproof-launch-title { font-family: 'Cinzel', serif; font-size: 10px; font-weight: 700; letter-spacing: 1px; color: #4a648c; }
      .adv-idproof-launch-sub { font-family: 'EB Garamond', serif; font-style: italic; font-size: 12px; color: rgba(74,100,140,0.6); margin-top: 2px; }
      .adv-idproof-launch-arrow { margin-left: auto; font-size: 18px; color: rgba(74,100,140,0.4); }

      .adv-detail-action-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(118px, 1fr)); gap: 8px; }
      .adv-action-tile { display: flex; flex-direction: column; align-items: center; gap: 5px; padding: 14px 10px; border: 1px solid; cursor: pointer; background: none; transition: all 0.2s; position: relative; overflow: hidden; clip-path: polygon(6px 0%,calc(100% - 6px) 0%,100% 6px,100% calc(100% - 6px),calc(100% - 6px) 100%,6px 100%,0 calc(100% - 6px),0 6px); }
      .adv-action-tile::before { content: ''; position: absolute; inset: 0; opacity: 0; transition: opacity 0.2s; }
      .adv-action-tile:hover::before { opacity: 1; }
      .adv-action-tile:hover { transform: translateY(-2px); }
      .adv-at-icon { font-size: 18px; position: relative; z-index: 1; }
      .adv-at-label { font-family: 'Cinzel', serif; font-size: 8.5px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; position: relative; z-index: 1; }
      .adv-at-desc { font-family: 'EB Garamond', serif; font-style: italic; font-size: 10.5px; position: relative; z-index: 1; opacity: 0.7; }
      .adv-action-tile.edit   { border-color: rgba(100,74,140,0.3); color: #6a4a8c; }
      .adv-action-tile.edit::before { background: rgba(100,74,140,0.07); }
      .adv-action-tile.edit:hover { border-color: rgba(100,74,140,0.55); box-shadow: 0 4px 18px rgba(100,74,140,0.15); }
      .adv-action-tile.approve { border-color: rgba(42,107,42,0.3); color: #2a6b2a; }
      .adv-action-tile.approve::before { background: rgba(42,107,42,0.07); }
      .adv-action-tile.approve:hover { border-color: rgba(42,107,42,0.55); box-shadow: 0 4px 18px rgba(42,107,42,0.15); }
      .adv-action-tile.reject { border-color: rgba(139,26,26,0.3); color: #8b1a1a; }
      .adv-action-tile.reject::before { background: rgba(139,26,26,0.07); }
      .adv-action-tile.reject:hover { border-color: rgba(139,26,26,0.55); box-shadow: 0 4px 18px rgba(139,26,26,0.15); }
      .adv-action-tile.warn { border-color: rgba(180,100,0,0.3); color: #b46400; }
      .adv-action-tile.warn::before { background: rgba(180,100,0,0.06); }
      .adv-action-tile.warn:hover { border-color: rgba(180,100,0,0.55); box-shadow: 0 4px 18px rgba(180,100,0,0.12); }
      .adv-action-tile.danger { border-color: rgba(139,26,26,0.22); color: rgba(139,26,26,0.8); }
      .adv-action-tile.danger::before { background: rgba(139,26,26,0.05); }
      .adv-action-tile.danger:hover { border-color: rgba(139,26,26,0.45); box-shadow: 0 4px 18px rgba(139,26,26,0.12); color: #8b1a1a; }
      .adv-action-tile:disabled { opacity: 0.35; cursor: not-allowed; transform: none !important; }

      .adv-idproof-body { flex: 1; overflow: auto; padding: 24px; display: flex; align-items: center; justify-content: center; min-height: 280px; position: relative; z-index: 1; }
      .adv-idproof-img-frame { position: relative; }
      .adv-idproof-img-glow { position: absolute; inset: -16px; background: radial-gradient(ellipse, rgba(201,151,58,0.1) 0%, transparent 65%); pointer-events: none; }
      .adv-idproof-img { max-width: 100%; max-height: 58vh; object-fit: contain; border: 1px solid rgba(201,151,58,0.2); box-shadow: 0 8px 32px rgba(26,4,4,0.12); position: relative; z-index: 1; }
      .adv-idproof-pdf { width: 100%; height: 58vh; border: none; }
      .adv-idproof-empty { text-align: center; }
      .adv-idproof-empty-icon { font-size: 40px; margin-bottom: 12px; opacity: 0.4; }
      .adv-idproof-empty p { font-family: 'EB Garamond', serif; font-style: italic; color: rgba(74,26,8,0.45); font-size: 15px; }

      /* ══ CONFIRM MODAL ══ */
      .adv-confirm-modal { position: relative; width: 100%; max-width: 420px; background: linear-gradient(160deg, #FDF3E3, #faf0dc 55%, #f5e8cc); border: 1px solid rgba(201,151,58,0.35); clip-path: polygon(0 18px,18px 0,calc(100% - 18px) 0,100% 18px,100% calc(100% - 18px),calc(100% - 18px) 100%,18px 100%,0 calc(100% - 18px)); box-shadow: 0 32px 90px rgba(26,4,4,0.68); animation: regalIn 0.3s cubic-bezier(0.23,1,0.32,1) both; overflow: hidden; }
      .adv-cm-topstrip { height: 3px; width: 100%; }
      .adv-cm-header { padding: 30px 28px 0; display: flex; flex-direction: column; align-items: center; text-align: center; gap: 10px; position: relative; z-index: 1; }
      .adv-cm-icon-wrap { position: relative; width: 68px; height: 68px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 1px solid; }
      .adv-cm-icon-ring { position: absolute; inset: -5px; border-radius: 50%; border: 1px dashed; animation: slowSpin 8s linear infinite; }
      .adv-cm-icon-ring2 { inset: -12px; animation-duration: 15s; animation-direction: reverse; }
      .adv-cm-icon { font-size: 24px; position: relative; z-index: 1; }
      .adv-cm-title-area { display: flex; flex-direction: column; align-items: center; gap: 4px; }
      .adv-cm-eyebrow { font-family: 'Cinzel', serif; font-size: 7px; letter-spacing: 3px; color: rgba(74,26,8,0.3); text-transform: uppercase; }
      .adv-cm-title { font-family: 'Cinzel Decorative', cursive; font-size: 20px; font-weight: 900; background: linear-gradient(135deg, var(--hero-1), var(--hero-2)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
      .adv-cm-subtitle { font-family: 'EB Garamond', serif; font-style: italic; font-size: 12.5px; color: rgba(74,26,8,0.38); }
      .adv-cm-divider { display: flex; align-items: center; justify-content: center; height: 1px; margin: 18px 36px 0; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.35), transparent); position: relative; }
      .adv-cm-divider-gem { width: 6px; height: 6px; transform: rotate(45deg); position: absolute; }
      .adv-cm-body { padding: 16px 32px 20px; position: relative; z-index: 1; }
      .adv-cm-message { font-family: 'EB Garamond', serif; font-size: 16px; color: var(--text-mid); line-height: 1.7; text-align: center; }
      .adv-cm-actions { display: flex; gap: 10px; justify-content: center; flex-wrap: wrap; padding: 0 28px 24px; position: relative; z-index: 1; }
      .adv-cm-confirm-btn { background: var(--cbg, linear-gradient(135deg,#1a4a1a,#2a6b2a)) !important; color: #e8ffe8 !important; border-color: var(--cborder, rgba(42,107,42,0.4)) !important; }
      .adv-cm-confirm-btn:hover { filter: brightness(1.12); }
      .adv-cm-bottomstrip { height: 1px; margin: 0 24px 14px; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.25), transparent); }

      /* ══ EDIT FORM ══ */
      .adv-edit-body { padding: 22px 28px 28px; position: relative; z-index: 1; }
      .adv-edit-section-label { font-family: 'Cinzel', serif; font-size: 8px; font-weight: 700; letter-spacing: 2.5px; text-transform: uppercase; color: var(--gold-dim); margin-bottom: 18px; padding-bottom: 7px; border-bottom: 1px solid rgba(201,151,58,0.15); }
      .adv-edit-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 20px; }
      .adv-edit-grid .full-width { grid-column: span 2; }
      .adv-edit-field { display: flex; flex-direction: column; gap: 5px; }
      .adv-edit-label { font-family: 'Cinzel', serif; font-size: 8.5px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--gold-dim); }
      .adv-req { color: #b52020; font-weight: 900; margin-left: 2px; }
      .adv-edit-wrap { display: flex; align-items: center; background: #fff9f0; border: 1px solid rgba(201,151,58,0.28); clip-path: polygon(10px 0%, calc(100% - 10px) 0%, 100% 50%, calc(100% - 10px) 100%, 10px 100%, 0% 50%); overflow: hidden; transition: border-color 0.3s, box-shadow 0.3s; }
      .adv-edit-wrap:focus-within { border-color: var(--gold-mid); background: #fffaf0; box-shadow: 0 0 0 3px rgba(201,151,58,0.12); }
      .adv-edit-wrap.err { border-color: #b52020; background: rgba(181,32,32,0.02); }
      .adv-edit-wrap-textarea { clip-path: polygon(10px 0%, calc(100% - 10px) 0%, 100% 10px, 100% calc(100% - 10px), calc(100% - 10px) 100%, 10px 100%, 0% calc(100% - 10px), 0% 10px); align-items: flex-start; }
      .adv-edit-prefix { font-family: 'Cinzel', serif; font-size: 11px; font-weight: 600; color: var(--text-mid); padding: 13px 12px 13px 20px; border-right: 1px solid rgba(201,151,58,0.2); flex-shrink: 0; }
      .adv-edit-sep { width: 1px; height: 20px; background: rgba(201,151,58,0.2); flex-shrink: 0; }
      .adv-edit-input { flex: 1; background: transparent; border: none; outline: none; font-family: 'EB Garamond', serif; font-size: 15px; color: var(--text-dark); padding: 13px 14px; }
      .adv-edit-input::placeholder { color: rgba(74,26,8,0.25); font-style: italic; }
      .adv-edit-textarea { flex: 1; background: transparent; border: none; outline: none; font-family: 'EB Garamond', serif; font-size: 15px; color: var(--text-dark); padding: 13px 14px; resize: vertical; min-height: 80px; line-height: 1.5; }
      .adv-edit-textarea::placeholder { color: rgba(74,26,8,0.25); font-style: italic; }
      .adv-edit-err { font-size: 10px; color: #b52020; font-family: 'EB Garamond', serif; font-style: italic; margin-top: 2px; }
      .adv-edit-hint { font-family: 'Cinzel', serif; font-size: 8px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; margin-top: 3px; }
      .adv-edit-hint.ok      { color: #2a6b2a; }
      .adv-edit-hint.taken   { color: #8b1a1a; }
      .adv-edit-hint.checking { color: var(--gold-dim); }
      .adv-edit-actions { display: flex; gap: 10px; align-items: center; justify-content: center; margin-top: 4px; padding-top: 18px; border-top: 1px solid rgba(201,151,58,0.12); }
      .adv-edit-save-btn { display: inline-flex; align-items: center; gap: 8px; font-family: 'Cinzel', serif; font-size: 10px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--crimson-deep); background: linear-gradient(135deg, #7a4e0c 0%, var(--gold-mid) 20%, var(--gold-bright) 40%, #fff8e7 55%, var(--gold-bright) 70%, var(--gold-mid) 85%, #6b4209 100%); border: none; padding: 13px 28px; cursor: pointer; clip-path: polygon(14px 0%, calc(100% - 14px) 0%, 100% 50%, calc(100% - 14px) 100%, 14px 100%, 0% 50%); box-shadow: 0 6px 24px rgba(201,151,58,0.3), inset 0 1px 0 rgba(255,255,255,0.25); transition: all 0.35s ease; position: relative; overflow: hidden; }
      .adv-edit-save-btn::before { content: ''; position: absolute; top: -50%; left: -70%; width: 40%; height: 200%; background: rgba(255,255,255,0.25); transform: skewX(-18deg); transition: left 0.55s ease; pointer-events: none; }
      .adv-edit-save-btn:hover::before { left: 130%; }
      .adv-edit-save-btn:hover { transform: translateY(-2px); box-shadow: 0 12px 32px rgba(201,151,58,0.5); filter: brightness(1.05); }
      .adv-edit-save-btn:disabled { opacity: 0.38; cursor: not-allowed; transform: none; box-shadow: none; }
      .adv-edit-saving-spinner { width: 12px; height: 12px; border-radius: 50%; border: 2px solid rgba(26,4,4,0.2); border-top-color: var(--crimson-deep); animation: spinIcon 0.7s linear infinite; flex-shrink: 0; }

      /* ══ TOAST — fixed to viewport ══ */
      .adv-toast-container { position: fixed; top: 28px; right: 28px; z-index: 2000; animation: toastIn 0.4s cubic-bezier(0.23,1,0.32,1) both; }
      @keyframes toastIn { from { opacity: 0; transform: translateX(60px) scale(0.95); } to { opacity: 1; transform: translateX(0) scale(1); } }
      .adv-toast-card { position: relative; width: 340px; background: linear-gradient(145deg,#FDF3E3,#faf0dc,#f5e8cc); border: 1px solid var(--border-color); clip-path: polygon(0 12px,12px 0,calc(100% - 12px) 0,100% 12px,100% calc(100% - 12px),calc(100% - 12px) 100%,12px 100%,0 calc(100% - 12px)); box-shadow: 0 12px 32px rgba(26,4,4,0.14), inset 0 1px 0 rgba(255,255,255,0.7); padding: 0 0 14px; overflow: hidden; }
      .adv-toast-top-bar { height: 3px; background: linear-gradient(90deg,transparent,var(--accent) 20%,var(--accent) 80%,transparent); margin-bottom: 16px; }
      .adv-toast-corner { position: absolute; width: 10px; height: 10px; border-color: rgba(201,151,58,0.45); border-style: solid; }
      .adv-toast-corner.tl { top: 12px; left: 12px; border-width: 1px 0 0 1px; }
      .adv-toast-corner.tr { top: 12px; right: 12px; border-width: 1px 1px 0 0; }
      .adv-toast-corner.bl { bottom: 8px; left: 12px; border-width: 0 0 1px 1px; }
      .adv-toast-corner.br { bottom: 8px; right: 12px; border-width: 0 1px 1px 0; }
      .adv-toast-medallion { position: absolute; left: 18px; top: 26px; width: 36px; height: 36px; border-radius: 50%; border: 1px solid; display: flex; align-items: center; justify-content: center; }
      .adv-toast-spin-ring { position: absolute; inset: -3px; border-radius: 50%; border: 1px dashed rgba(201,151,58,0.3); animation: slowSpin 8s linear infinite; }
      .adv-toast-icon-inner { font-size: 14px; position: relative; z-index: 1; }
      .adv-toast-content { padding: 0 40px 0 66px; }
      .adv-toast-label-row { display: flex; align-items: center; gap: 6px; margin-bottom: 5px; }
      .adv-toast-symbol { font-family: 'Cinzel Decorative', cursive; font-size: 9px; opacity: 0.7; }
      .adv-toast-title { font-family: 'Cinzel', serif; font-size: 10px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; flex: 1; }
      .adv-toast-rule { height: 1px; margin-bottom: 7px; }
      .adv-toast-message { font-family: 'EB Garamond', serif; font-style: italic; font-size: 14px; color: rgba(74,26,8,0.75); line-height: 1.5; }
      .adv-toast-bottom-rule { height: 1px; margin: 12px 20px 0; background: linear-gradient(90deg,transparent,rgba(201,151,58,0.25),transparent); }
      .adv-toast-close { position: absolute; top: 22px; right: 14px; background: none; border: none; font-size: 13px; cursor: pointer; opacity: 0.45; transition: opacity 0.2s; }
      .adv-toast-close:hover { opacity: 0.9; }

      /* ══ SHOW/HIDE ══ */
      .adv-desktop-only { display: table; }
      .adv-mobile-only  { display: none !important; }
      @media (max-width: 768px) {
        .adv-desktop-only { display: none !important; }
        .adv-mobile-only  { display: flex !important; }
      }

      /* ══ MOBILE CARDS ══ */
      .adv-card-list { display: flex; flex-direction: column; gap: 12px; padding-top: 4px; }
      .adv-mob-card { background: linear-gradient(145deg, #FDF3E3, #faf0dc); border: 1px solid rgba(201,151,58,0.2); clip-path: polygon(0 10px,10px 0,calc(100% - 10px) 0,100% 10px,100% calc(100% - 10px),calc(100% - 10px) 100%,10px 100%,0 calc(100% - 10px)); box-shadow: 0 2px 12px rgba(26,4,4,0.08); animation: rowIn 0.35s ease both; overflow: hidden; position: relative; }
      .adv-mob-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.5), transparent); }
      .adv-mob-card-header { display: flex; align-items: center; gap: 12px; padding: 14px 14px 10px; border-bottom: 1px solid rgba(201,151,58,0.1); }
      .adv-mob-avatar { width: 42px; height: 42px; border-radius: 50%; flex-shrink: 0; background: radial-gradient(circle, var(--crimson-rich), var(--crimson-deep)); border: 1.5px solid rgba(232,196,106,0.3); display: flex; align-items: center; justify-content: center; font-family: 'Cinzel Decorative', cursive; font-size: 15px; color: var(--gold-bright); box-shadow: 0 0 12px rgba(232,196,106,0.12); }
      .adv-mob-card-info { flex: 1; min-width: 0; }
      .adv-mob-name { font-family: 'Cinzel', serif; font-size: 12px; font-weight: 700; letter-spacing: 0.5px; color: var(--text-dark); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      .adv-mob-mobile { font-family: 'Cinzel', serif; font-size: 10px; letter-spacing: 0.5px; color: rgba(74,26,8,0.55); margin-top: 2px; }
      .adv-mob-email { font-family: 'EB Garamond', serif; font-style: italic; font-size: 11.5px; color: rgba(74,26,8,0.4); margin-top: 1px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      .adv-mob-meta { padding: 10px 14px; display: flex; flex-direction: column; gap: 5px; border-bottom: 1px solid rgba(201,151,58,0.08); }
      .adv-mob-meta-item { display: flex; align-items: flex-start; gap: 7px; font-family: 'EB Garamond', serif; font-size: 13px; color: rgba(74,26,8,0.6); }
      .adv-mob-meta-icon { color: var(--gold-dim); font-size: 11px; margin-top: 2px; flex-shrink: 0; }
      .adv-mob-address-item span { font-size: 12.5px; line-height: 1.4; }
      .adv-mob-actions { display: flex; gap: 6px; flex-wrap: wrap; padding: 10px 14px 12px; }
      .adv-mob-btn { display: inline-flex; align-items: center; gap: 5px; font-family: 'Cinzel', serif; font-size: 8.5px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; border: 1px solid; padding: 7px 12px; cursor: pointer; background: none; transition: all 0.18s; clip-path: polygon(6px 0%,calc(100% - 6px) 0%,100% 50%,calc(100% - 6px) 100%,6px 100%,0 50%); }
      .adv-mob-btn.mob-view    { border-color: rgba(201,151,58,0.35); color: var(--gold-dim); }
      .adv-mob-btn.mob-view:hover { background: rgba(201,151,58,0.1); }
      .adv-mob-btn.mob-edit    { border-color: rgba(100,74,140,0.35); color: #6a4a8c; }
      .adv-mob-btn.mob-edit:hover { background: rgba(100,74,140,0.1); }
      .adv-mob-btn.mob-idproof { border-color: rgba(74,100,140,0.35); color: #4a648c; }
      .adv-mob-btn.mob-idproof:hover { background: rgba(74,100,140,0.1); }
      .adv-mob-btn.mob-approve { border-color: rgba(42,107,42,0.35); color: #2a6b2a; }
      .adv-mob-btn.mob-approve:hover { background: rgba(42,107,42,0.1); }
      .adv-mob-btn.mob-reject  { border-color: rgba(139,26,26,0.35); color: #8b1a1a; }
      .adv-mob-btn.mob-reject:hover { background: rgba(139,26,26,0.1); }
      .adv-mob-btn.mob-warn    { border-color: rgba(180,100,0,0.35); color: #b46400; }
      .adv-mob-btn.mob-warn:hover { background: rgba(180,100,0,0.08); }
      .adv-mob-btn.mob-delete  { border-color: rgba(139,26,26,0.22); color: rgba(139,26,26,0.7); margin-left: auto; }
      .adv-mob-btn.mob-delete:hover { background: rgba(139,26,26,0.08); color: #8b1a1a; }

      @media (max-width: 900px) { .adv-hero-rings { display: none; } }

      @media (max-width: 768px) {
        .adv-hero { padding: 22px 16px 0; border-radius: 0; }
        .adv-hero-medallion { width: 68px; height: 68px; margin-bottom: 12px; }
        .adv-hero-om { font-size: 24px; }
        .adv-hero-logo-img { width: 40px; height: 40px; }
        .adv-hero-eyebrow { font-size: 7px; letter-spacing: 1.2px; }
        .adv-hero-title { font-size: 19px; }
        .adv-hero-sub { font-size: 13px; margin-bottom: 12px; }
        .adv-stat-row { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; width: 100%; padding: 0 0 18px; }
        .adv-stat-card { min-width: unset; padding: 12px 10px; clip-path: none; border-radius: 8px; }

        .adv-toolbar { padding: 12px 14px 10px; gap: 9px; flex-direction: column; align-items: stretch; }
        .adv-search-wrap { min-width: unset; width: 100%; clip-path: none; border-radius: 28px; }
        .adv-dropdown-wrap { width: 100%; }
        .adv-dropdown-trigger { width: 100%; min-width: unset; clip-path: none; border-radius: 28px; justify-content: space-between; padding: 11px 16px; }
        .adv-dropdown-menu { left: 0; right: 0; transform: none; clip-path: none; border-radius: 12px; min-width: unset; width: 100%; }
        @keyframes ddIn { from { opacity:0; transform: translateY(-6px) scale(0.98); } to { opacity:1; transform: translateY(0) scale(1); } }
        .adv-toolbar-btns { width: 100%; display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-left: 0; }
        .adv-action-btn { clip-path: none; border-radius: 28px; justify-content: center; padding: 11px 10px; font-size: 9px; }
        .adv-table-wrap { padding: 0 14px 40px; }

        /* Modals slide up from bottom */
        .adv-modal-overlay { padding: 0; align-items: flex-end; }
        .adv-regal-modal { clip-path: none !important; border-radius: 22px 22px 0 0 !important; max-height: 93vh; border-bottom: none; width: 100%; max-width: 100% !important; }
        .adv-regal-modal::after { content: ''; position: absolute; top: 8px; left: 50%; transform: translateX(-50%); width: 44px; height: 4px; border-radius: 2px; background: rgba(232,196,106,0.28); pointer-events: none; z-index: 20; display: block; }
        .adv-rm-header { padding: 26px 16px 20px; }
        .adv-detail-scroll-body { padding: 16px 16px 28px; }
        .adv-detail-info-grid { grid-template-columns: 1fr; gap: 8px; }
        .adv-detail-action-grid { grid-template-columns: 1fr 1fr; gap: 8px; }
        .adv-idproof-launch-btn { clip-path: none; border-radius: 10px; }
        .adv-edit-body { padding: 16px 16px 28px; }
        .adv-edit-grid { grid-template-columns: 1fr; gap: 12px; }
        .adv-edit-grid .full-width { grid-column: span 1; }
        .adv-edit-wrap { clip-path: none; border-radius: 28px; }
        .adv-edit-wrap-textarea { clip-path: none; border-radius: 14px; }
        .adv-edit-save-btn { clip-path: none; border-radius: 28px; flex: 1; justify-content: center; }
        .adv-edit-actions .adv-btn { clip-path: none; border-radius: 28px; }
        .adv-confirm-modal { clip-path: none !important; border-radius: 22px 22px 0 0 !important; max-width: 100%; width: 100%; border-bottom: none; }
        .adv-btn { clip-path: none; border-radius: 28px; padding: 12px 18px; font-size: 9.5px; }
        .adv-toast-container { top: auto; bottom: 14px; right: 14px; left: 14px; }
        .adv-toast-card { width: 100%; clip-path: none; border-radius: 12px; }
      }

      @media (max-width: 390px) {
        .adv-hero-title { font-size: 16px; }
        .adv-hero-eyebrow { display: none; }
        .adv-at-desc { display: none; }
      }
    `}</style>
  );
}